import React, { useState, useEffect } from 'react';
import { Copy } from 'lucide-react';

export function GenerateKeyForm({ 
  teamProducts = [], 
  selectedProduct,
  setSelectedProduct, 
  plans = [], 
  selectedPlan,
  setSelectedPlan,
  loadingPlans,
  fetchPlans, // Pass fetchPlans function
  handleGenerateKey, // Pass generate key handler
  isGenerating = false, // Whether a key generation request is in-flight
  keyForm, // Pass form state
  setKeyForm // Pass form state setter
}) {

  // Reset plan when product changes
  useEffect(() => {
    if (selectedProduct) {
      fetchPlans(selectedProduct.id);
    } else {
      setSelectedPlan(null);
      // Optionally clear plans array here if fetchPlans doesn't do it
      // setPlans([]); 
    }
  }, [selectedProduct, fetchPlans]); // Add fetchPlans dependency

  const copyToClipboard = async (value) => {
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = value;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error('Failed to copy value to clipboard', error);
    }
  };

  return (
    <div className="bg-slate-900/50 border border-slate-700 p-4 rounded-lg mt-6">
      <h3 className="text-base font-semibold mb-4 text-slate-100">Generate New API Key</h3>
      <div className="space-y-4">
        {/* Product Selection */}
        <div>
          <label className="block text-sm mb-1 text-slate-400">Product *</label>
          <select
            className="w-full p-2 bg-slate-800 rounded border border-slate-700 text-slate-200 [&>option]:bg-slate-800 [&>option]:text-slate-200 focus:outline-none focus:border-slate-600 disabled:opacity-50"
            value={selectedProduct?.id || ''}
            onChange={(e) => {
              const productId = e.target.value;
              const product = teamProducts.find(p => p.id === productId);
              setSelectedProduct(product);
            }}
            disabled={!teamProducts.length}
          >
            <option value="" disabled={!!selectedProduct}>{teamProducts.length ? 'Select a product...' : 'No products available'}</option>
            {Array.isArray(teamProducts) && teamProducts.map(product => (
              <option key={product.id} value={product.id}>
                {`${product.name} · ${product.id}`}
              </option>
            ))}
          </select>
          {selectedProduct && (
            <div className="mt-2 text-xs text-slate-400 flex items-center gap-2">
              <span className="font-mono">Product ID: {selectedProduct.id}</span>
              <button
                type="button"
                onClick={() => copyToClipboard(selectedProduct.id)}
                className="p-1 rounded hover:bg-slate-700 text-slate-400 hover:text-slate-200 transition"
                title="Copy product ID"
              >
                <Copy size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Plan Selection */}
        {selectedProduct && (
          <div>
            <label className="block text-sm mb-1 text-slate-400">Plan *</label>
            <select
              className="w-full p-2 bg-slate-800 rounded border border-slate-700 text-slate-200 [&>option]:bg-slate-800 [&>option]:text-slate-200 focus:outline-none focus:border-slate-600 disabled:opacity-50"
              value={selectedPlan?.id || ''}
              onChange={(e) => {
                const planId = e.target.value;
                const plan = plans.find(p => p.id === planId);
                setSelectedPlan(plan);
              }}
              disabled={loadingPlans || !plans.length || !selectedProduct}
            >
              <option value="" disabled={!!selectedPlan || loadingPlans}>
                {loadingPlans ? 'Loading plans...' : (plans.length ? 'Select a plan...' : 'No plans available')}
              </option>
              {plans.map(plan => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Nickname Input */}
        <div>
          <label className="block text-sm mb-1 text-slate-400">Key Nickname *</label>
          <input
            type="text"
            placeholder="e.g., Production Server Key"
            className="w-full p-2 bg-slate-800 rounded border border-slate-700 focus:outline-none focus:border-slate-600 text-slate-100 placeholder-slate-500"
            value={keyForm.nickname}
            onChange={(e) => setKeyForm({ ...keyForm, nickname: e.target.value })}
          />
        </div>

        {/* Usage limit Input */}
        <div>
          <label className="block text-sm mb-1 text-slate-400">Usage limit (units)</label>
          <input
            type="number"
            placeholder="Leave blank for unlimited"
            className="w-full p-2 bg-slate-800 rounded border border-slate-700 focus:outline-none focus:border-slate-600 text-slate-100 placeholder-slate-500"
            value={keyForm.usageLimit ?? ''}
            onChange={(e) => setKeyForm({ ...keyForm, usageLimit: e.target.value })}
            min="1"
          />
          <p className="mt-1 text-xs text-slate-500">
            Caps lifetime consumption. Once reached, the key stops validating and stops recording usage — handy for trial keys.
          </p>
        </div>

        {/* TTL Input */}
        <div>
          <label className="block text-sm mb-1 text-slate-400">Cache TTL (hours)</label>
          <input
            type="number"
            className="w-full p-2 bg-slate-800 rounded border border-slate-700 focus:outline-none focus:border-slate-600 text-slate-100"
            value={keyForm.ttl}
            onChange={(e) => setKeyForm({ ...keyForm, ttl: parseInt(e.target.value) || 1 })}
            min="1"
          />
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerateKey}
          disabled={!selectedProduct || !selectedPlan || !keyForm.nickname || loadingPlans || isGenerating}
          className="w-full py-2 bg-indigo-600 rounded hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-white font-medium text-sm"
        >
          {isGenerating ? 'Generating...' : 'Generate API Key'}
        </button>
      </div>
    </div>
  );
}
